import {
  Table as TableInstance,
  ColumnFiltersState,
  OnChangeFn,
  Row,
} from '@tanstack/react-table';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  forwardRef,
  ForwardedRef,
} from 'react';
import {
  FieldArray,
  FieldArrayPath,
  FieldValues,
  useFieldArray,
  useFormContext,
  useFormState,
} from 'react-hook-form';
import {
  Table,
  TableBaseProps,
  TableProps,
  TableStickyAndVirtualProps,
} from '../../../tanstacktable';
import { WithFilterOptionsProps } from '../../../tanstacktable/renderers/WithFilterOptions';
import { useUnifiedRef } from '../../../useUnifiedRef';
import {
  useAddFormFieldArray,
  useRemoveFormFieldArray,
} from '../../AdditionalFormContext';
import { ROW_UNIQUE_KEY_NAME } from '../../../utils/randomUniqueKey';
import { AutoScrollToFocusedRow } from './AutoScrollToFocusedRow';
import { useSearchParams } from 'react-router-dom';

export type TanstackFormTableProps<
  FormData extends FieldValues = FieldValues,
  Name extends FieldArrayPath<FormData> = FieldArrayPath<FormData>,
  RowData extends FieldArray<FormData, Name> = FieldArray<FormData, Name>,
> = TableBaseProps<RowData> &
  WithFilterOptionsProps &
  TableStickyAndVirtualProps & {
    name: Name;
    sequenceNumberFieldName?: string;
    hideClearFilters?: boolean;
    hideHeader?: boolean;
    onFilter?: OnChangeFn<ColumnFiltersState>;
    prettyDisplayErrors?: boolean;
  };

export const TanstackFormTable = forwardRef(
  <
    FormData extends FieldValues = FieldValues,
    Name extends FieldArrayPath<FormData> = FieldArrayPath<FormData>,
    RowData extends FieldArray<FormData, Name> = FieldArray<FormData, Name>,
  >(
    props: TanstackFormTableProps<FormData, Name, RowData>,
    forwardedRef: ForwardedRef<TableInstance<RowData>>,
  ) => {
    const {
      name,
      sequenceNumberFieldName = 'sequenceNumber',
      onSort,
      columns,
      filters,
      onFilter,
      prettyDisplayErrors = false,
      ...rest
    } = props;

    const fieldArray = useFieldArray<
      FormData,
      Name,
      typeof ROW_UNIQUE_KEY_NAME
    >({
      name,
      keyName: ROW_UNIQUE_KEY_NAME,
    });
    const addFieldArray = useAddFormFieldArray();
    const removeFieldArray = useRemoveFormFieldArray();
    const [isCached, setIsCached] = useState(false);
    const [filter, setFilter] = useState<ColumnFiltersState>(filters || []);
    const internalRef = useRef<TableInstance<RowData> | null>(null);
    const unifiedRef = useUnifiedRef(forwardedRef, internalRef);

    const { errors } = useFormState();

    const [searchParams] = useSearchParams();
    const errorFilterColumns = useMemo(() => {
      return searchParams.get('errorFilterColumns')?.split(',') || [];
    }, [searchParams]);

    const clearErrorFilterColumns = useCallback(() => {
      searchParams.delete('errorFilterColumns');
    }, [searchParams]);

    const errorInfo = useMemo(() => {
      if (!prettyDisplayErrors) return null;

      return getErrorInfo(errors, name);
    }, [prettyDisplayErrors, errors, name]);

    const globalFilterFn = useCallback(
      (row: Row<RowData>) => {
        if (!errorInfo) return true;

        for (const colId of errorFilterColumns) {
          const hasErrorInColumn = errorInfo.cellErrors.has(
            `${row.index}:${colId}`,
          );
          if (!hasErrorInColumn) return false;
        }
        return true;
      },
      [errorInfo, errorFilterColumns],
    );

    const hasAnyErrorFilter = errorFilterColumns.length > 0;

    const handleFilterChange = useCallback<OnChangeFn<ColumnFiltersState>>(
      (updaterOrValue) => {
        setFilter((prev) => {
          const newValue =
            typeof updaterOrValue === 'function'
              ? updaterOrValue(prev)
              : updaterOrValue;

          // Clear error filter columns when all filters are cleared
          if (newValue.length === 0 && hasAnyErrorFilter) {
            clearErrorFilterColumns();
          }

          onFilter?.(newValue);
          return newValue;
        });
      },
      [hasAnyErrorFilter, onFilter, clearErrorFilterColumns],
    );

    useEffect(() => {
      addFieldArray(name, fieldArray);
      setIsCached(true);
      return () => {
        removeFieldArray(name);
        setIsCached(false);
      };
    }, [name, fieldArray, addFieldArray, removeFieldArray]);

    const { setValue, getValues } = useFormContext();

    const handleSort: Required<TableProps<RowData>>['onSort'] = useCallback(
      (sorting) => {
        setValue(name, getValues(name));
        onSort?.(sorting);
      },
      [onSort, setValue, getValues, name],
    );

    const hasSequenceNumberColumn = useMemo(
      () =>
        columns.some(
          ({ accessorKey, id }: any) =>
            (accessorKey || id) === sequenceNumberFieldName,
        ),
      [columns, sequenceNumberFieldName],
    );

    const tableContent = (
      <FormTableContext.Provider
        value={{ name, hasSequenceNumberColumn, sequenceNumberFieldName }}
      >
        {isCached && (
          <Table
            ref={unifiedRef.ref}
            {...rest}
            filters={filter}
            onFilter={handleFilterChange}
            columns={columns}
            onSort={handleSort}
            name={name}
            rowKey={ROW_UNIQUE_KEY_NAME}
            data={fieldArray.fields as RowData[]}
            globalFilterFn={hasAnyErrorFilter ? globalFilterFn : undefined}
            globalFilter={hasAnyErrorFilter ? true : undefined}
          >
            <AutoScrollToFocusedRow />
          </Table>
        )}
      </FormTableContext.Provider>
    );

    if (prettyDisplayErrors) {
      return (
        <FormTableErrorContext.Provider value={errorInfo}>
          {tableContent}
        </FormTableErrorContext.Provider>
      );
    }

    return tableContent;
  },
);

const FormTableContext = createContext<{
  name: string;
  hasSequenceNumberColumn: boolean;
  sequenceNumberFieldName: string;
}>(undefined!);

export const useFormTableName = () => {
  const context = useContext(FormTableContext);

  if (!context) {
    throw new Error(
      'useFormTableName must be consumed within a FormTable(Context)',
    );
  }

  return context.name;
};

export const useFormTableSequenceNumberColumn = (ignoreSequence = false) => {
  const context = useContext(FormTableContext);

  if (!context && ignoreSequence) {
    return {
      hasSequenceNumberColumn: false,
      sequenceNumberFieldName: undefined,
    };
  }
  if (!context) {
    throw new Error(
      'useFormTableName must be consumed within a FormTable(Context)',
    );
  }

  const { hasSequenceNumberColumn, sequenceNumberFieldName } = context;
  return { hasSequenceNumberColumn, sequenceNumberFieldName };
};

export interface FormTableErrorInfo {
  rowIndicesWithErrors: Set<number>;
  columnErrorCounts: Map<string, number>;
  cellErrors: Set<string>;
}

const FormTableErrorContext = createContext<FormTableErrorInfo | null>(null);
export const useFormTableErrors = () => useContext(FormTableErrorContext);

export function getErrorInfo(
  errors: Record<string, any> | undefined,
  fieldArrayName: string,
): FormTableErrorInfo {
  const rowIndicesWithErrors = new Set<number>();
  const columnErrorCounts = new Map<string, number>();
  const cellErrors = new Set<string>();

  const rowErrors = errors?.[fieldArrayName];
  if (!rowErrors) {
    return { rowIndicesWithErrors, columnErrorCounts, cellErrors };
  }

  const processRowError = (rowError: Record<string, any>, index: number) => {
    if (!rowError || typeof rowError !== 'object') return;

    const errorFields = Object.keys(rowError);
    if (errorFields.length > 0) {
      rowIndicesWithErrors.add(index);

      for (const fieldName of errorFields) {
        if (rowError[fieldName]) {
          cellErrors.add(`${index}:${fieldName}`);
          columnErrorCounts.set(
            fieldName,
            (columnErrorCounts.get(fieldName) || 0) + 1,
          );
        }
      }
    }
  };

  if (Array.isArray(rowErrors)) {
    for (let i = 0; i < rowErrors.length; i++) {
      if (rowErrors[i]) processRowError(rowErrors[i], i);
    }
  } else {
    for (const key in rowErrors) {
      const index = parseInt(key, 10);
      if (!isNaN(index)) {
        processRowError(rowErrors[key], index);
      }
    }
  }

  return { rowIndicesWithErrors, columnErrorCounts, cellErrors };
}
